/**
 * Data Service Layer - Handles API calls to real backend service
 * UPDATED: Now uses real AWS Lambda backend instead of local file service
 */

// Import apiClient for standardized API calls
import apiClient from './apiClient';

class DataService {
  constructor() {
    console.log('🔄 DataService initialized with real backend API:', apiClient.defaults.baseURL);
  }
  
  // Generate unique ID
  generateId(type) {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `${type}_${timestamp}_${random}`;
  }

  // Add or Update connection based on account type
  async saveConnection(accountType, connectionData, isEdit = false, editId = null) {
    // Normalize accountType for Solo users
    let normalizedType = accountType;
    if (["Solo", "solo", "Individual", "individual"].includes(accountType)) {
      normalizedType = "solo";
    }
    // Always use backend API for saving connections
    return this.saveConnectionAPI(normalizedType, connectionData, isEdit, editId);
  }

  // Remove connection
  async removeConnection(accountType, connectionId) {
    return this.removeConnectionAPI(accountType, connectionId);
  }

  // Get connections by account type
  async getConnections(accountType) {
    // Always use API to get connections from server
    return await this.getConnectionsAPI(accountType);
  }

  // Get connections where the current user appears as a member (not owner)
  async getMemberTeamConnections() {
    try {
      const response = await apiClient.get('/api/connections/as-member');
      return response.data.success ? response.data.connections : [];
    } catch (error) {
      console.error('getMemberTeamConnections failed:', error.message);
      return [];
    }
  }

  // API method to get connections from server
  async getConnectionsAPI(accountType) {
    try {
      const response = await apiClient.get(`/api/connections/${accountType}`);
      if (response.data.success) {
        return response.data.connections;
      }
      console.warn(`getConnectionsAPI: server error for ${accountType}:`, response.data.message);
      return [];
    } catch (error) {
      // Network-level failures (dropped connection, timeout) are transient — warn, don't error
      if (!error.response) {
        console.warn(`getConnectionsAPI: network error for ${accountType}:`, error.message);
      } else {
        console.error(`getConnectionsAPI: ${error.response.status} for ${accountType}:`, error.response.data);
      }
      return [];
    }
  }

  // JSON-based operations (for frontend development)
  saveConnectionJSON(accountType, connectionData, isEdit = false, editId = null) {
    return new Promise((resolve) => {
      setTimeout(() => { // Simulate network delay
        try {
          const typeMap = {
            'small_team': 'smallTeamConnections',
            'large_team': 'largeTeamConnections',
            'solo_assistant': 'soloAgents',
            'individual': 'individuals'
          };
          const dataKey = typeMap[accountType];
          if (!this.data[dataKey]) {
            this.data[dataKey] = [];
          }

          if (isEdit && editId) {
            // Update existing connection
            const index = this.data[dataKey].findIndex(item => item.id === editId);
            if (index !== -1) {
              this.data[dataKey][index] = {
                ...this.data[dataKey][index],
                ...connectionData,
                updated_at: new Date().toISOString()
              };
              
              
              resolve({
                success: true,
                message: `${accountType.replace('_', ' ')} connection updated successfully`,
                data: this.data[dataKey][index]
              });
            } else {
              resolve({
                success: false,
                message: 'Connection not found for update'
              });
            }
          } else {
            // Add new connection
            const newConnection = {
              id: this.generateId(accountType),
              ...connectionData,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              status: 'active'
            };
            
            this.data[dataKey].push(newConnection);
            
            
            resolve({
              success: true,
              message: `${accountType.replace('_', ' ')} connection created successfully`,
              data: newConnection
            });
          }
        } catch (error) {
          resolve({
            success: false,
            message: 'Failed to save connection',
            error: error.message
          });
        }
      }, 100); // Simulate network delay
    });
  }

  removeConnectionJSON(accountType, connectionId) {
    return new Promise((resolve) => {
      setTimeout(() => {
        try {
          const typeMap = {
            'small_team': 'smallTeamConnections',
            'large_team': 'largeTeamConnections',
            'solo_assistant': 'soloAgents',
            'individual': 'individuals'
          };
          const dataKey = typeMap[accountType];
          const index = this.data[dataKey].findIndex(item => item.id === connectionId);
          
          if (index !== -1) {
            const removedItem = this.data[dataKey].splice(index, 1)[0];
            
            
            resolve({
              success: true,
              message: `${accountType.replace('_', ' ')} connection removed successfully`,
              data: removedItem
            });
          } else {
            resolve({
              success: false,
              message: 'Connection not found'
            });
          }
        } catch (error) {
          resolve({
            success: false,
            message: 'Failed to remove connection',
            error: error.message
          });
        }
      }, 100);
    });
  }

  // API-based operations (for backend integration)
  async saveConnectionAPI(accountType, connectionData, isEdit = false, editId = null) {
    try {
      const endpoint = isEdit 
        ? `/api/connections/${accountType}/${editId}`
        : `/api/connections/${accountType}`;
      
      const method = isEdit ? 'put' : 'post';
      
      const response = await apiClient[method](endpoint, {
        ...connectionData,
        account_type: accountType
      });

      if (response.data.success) {
        console.log(`✅ Connection ${isEdit ? 'updated' : 'created'} successfully`);
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Backend API save failed:', error);
      return {
        success: false,
        message: 'Backend API call failed',
        error: error.response?.data?.message || error.message
      };
    }
  }

  // Retrieve active task lists for a list of member emails, scoped to a specific connection.
  // connectionId (optional): when provided, only records for that connection are returned.
  // Returns { tasks: [...], loadStatus: string } on success, null on network/server error.
  async getTasksByMembers(emails, connectionId) {
    try {
      console.log(`[DEBUG] getTasksByMembers: fetching tasks for ${emails.length} member(s)${connectionId ? ` (connection: ${connectionId})` : ''}`);
      const body = { emails };
      if (connectionId) body.connection_id = connectionId;
      const response = await apiClient.post('/api/tasks/members', body);

      console.log(`[DEBUG] getTasksByMembers: response status:`, response.status);
      console.log(`[DEBUG] getTasksByMembers: response data:`, response.data);

      if (response.data.success) {
        console.log(`📖 Loaded ${response.data.count} task records from backend API`);
        return {
          tasks: response.data.tasks,
          loadStatus: response.data.load_status || (response.data.tasks.length > 0 ? 'tasks_found' : 'no_task_records'),
        };
      } else {
        console.error('❌ Failed to load tasks:', response.data.message);
        return null; // null signals caller to fall back to local data
      }
    } catch (error) {
      console.error('❌ getTasksByMembers API call failed:', error.message);
      if (error.response) {
        console.error('❌ Error response status:', error.response.status);
        console.error('❌ Error response data:', error.response.data);
      }
      return null; // null signals caller to fall back to local data
    }
  }

  // Retrieve all records with has_unpublished_changes=true within the caller's scope.
  // Account Owner: all member records across owned connections (+ owner's own).
  // targetEmail: when impersonating an owner, pass their email so the backend scopes
  //   to that user's connections instead of the JWT caller's.
  // Returns { tasks: [...], loadStatus: string } on success, null on error.
  async getPendingChanges(targetEmail = null) {
    try {
      const url = targetEmail
        ? `/api/tasks/pending-changes?target_email=${encodeURIComponent(targetEmail)}`
        : '/api/tasks/pending-changes';
      const response = await apiClient.get(url);
      if (response.data.success) {
        console.log(`📖 Loaded ${response.data.count} pending-change records from backend API`);
        return {
          tasks: response.data.tasks,
          loadStatus: response.data.load_status || (response.data.count > 0 ? 'tasks_found' : 'no_task_records'),
        };
      }
      console.error('❌ Failed to load pending changes:', response.data.message);
      return null;
    } catch (error) {
      console.error('❌ getPendingChanges API call failed:', error.message);
      return null;
    }
  }

  // Retrieve all active task lists for connections owned by the current user.
  // Returns { tasks: [...], loadStatus: string } on success, null on network/server error.
  async getOwnerTasks() {
    try {
      const response = await apiClient.get('/api/tasks/owner');
      if (response.data.success) {
        console.log(`📖 Loaded ${response.data.count} owner task records from backend API`);
        return {
          tasks: response.data.tasks,
          loadStatus: response.data.load_status || (response.data.tasks.length > 0 ? 'tasks_found' : 'no_task_records'),
        };
      } else {
        console.error('❌ Failed to load owner tasks:', response.data.message);
        return null; // null signals caller to fall back to Strategy 2
      }
    } catch (error) {
      console.error('❌ getOwnerTasks API call failed:', error.message);
      return null; // null signals caller to fall back to Strategy 2
    }
  }

  // Persist a single edited task to every DynamoDB record sharing the same task_list_title
  async updateTaskItem(taskListTitle, taskIndex, task) {
    try {
      const response = await apiClient.put('/api/tasks/update', {
        task_list_title: taskListTitle,
        task_index: taskIndex,
        task
      });
      if (response.data.success) {
        console.log(`✅ Task saved: updated ${response.data.updated_count} record(s) in DynamoDB`);
        return true;
      } else {
        console.error('❌ Failed to save task:', response.data.message);
        return false;
      }
    } catch (error) {
      console.error('❌ updateTaskItem API call failed:', error.message);
      return false;
    }
  }

  // Draft-save a single task edit to the editor's own record only (no team sync).
  // hasUnpublishedChanges / unpublishedChangeCount: the recalculated net dirty state after the edit.
  // When provided the backend SETs the exact values instead of blindly incrementing the counter.
  async updateTaskItemDraft(taskListTitle, taskIndex, task, hasUnpublishedChanges, unpublishedChangeCount) {
    try {
      const body = {
        task_list_title: taskListTitle,
        task_index: taskIndex,
        task,
        draft_only: true,
      };
      if (hasUnpublishedChanges !== undefined && unpublishedChangeCount !== undefined) {
        body.has_unpublished_changes  = hasUnpublishedChanges;
        body.unpublished_change_count = unpublishedChangeCount;
      }
      const response = await apiClient.put('/api/tasks/update', body);
      if (response.data.success) {
        console.log(`📝 Draft saved: updated ${response.data.updated_count} record(s)`);
        return true;
      } else {
        console.error('❌ Failed to draft-save task:', response.data.message);
        return false;
      }
    } catch (error) {
      console.error('❌ updateTaskItemDraft API call failed:', error.message);
      return false;
    }
  }

  // Push the editor's full tasks array to all team members' records and clear draft metadata
  async propagateTasks(taskListTitle, tasks) {
    try {
      const response = await apiClient.put('/api/tasks/propagate', {
        task_list_title: taskListTitle,
        tasks,
      });
      if (response.data.success) {
        console.log(`🔄 Tasks propagated to ${response.data.updated_count} record(s) for "${taskListTitle}"`);
        return true;
      } else {
        console.error('❌ Failed to propagate tasks:', response.data.message);
        return false;
      }
    } catch (error) {
      console.error('❌ propagateTasks API call failed:', error.message);
      return false;
    }
  }

  // Sync saved tasks to all team members' calendars.
  // selectedRecipients: array of emails to receive SmartClose updates + calendar sync.
  async publishTasks(recipientEmail, taskListTitle, connectionId, selectedRecipients) {
    try {
      const body = { recipient_email: recipientEmail, task_list_title: taskListTitle };
      if (connectionId) body.connection_id = connectionId;
      if (selectedRecipients) body.selected_recipients = selectedRecipients;
      const response = await apiClient.post('/api/tasks/publish', body);
      if (response.data.success) {
        console.log(`📅 Tasks published to calendars for "${taskListTitle}"`);
        return true;
      } else {
        console.error('❌ Failed to publish tasks:', response.data.message);
        return false;
      }
    } catch (error) {
      console.error('❌ publishTasks API call failed:', error.message);
      return false;
    }
  }

  // Discard unpublished draft changes and restore the task list to the last published version.
  // restoreTasks: optional frontend-side published snapshot; when provided the backend uses it
  // directly (fast path). When omitted the backend looks up the connection_source record.
  async discardDraft(taskListTitle, restoreTasks) {
    try {
      const body = { task_list_title: taskListTitle };
      if (Array.isArray(restoreTasks)) body.restore_tasks = restoreTasks;
      const response = await apiClient.put('/api/tasks/discard', body);
      if (response.data.success) {
        return { success: true, tasks: response.data.tasks || [] };
      } else {
        console.error('❌ Failed to discard draft:', response.data.message);
        return { success: false };
      }
    } catch (error) {
      console.error('❌ discardDraft API call failed:', error.message);
      return { success: false };
    }
  }

  // Archive a task list — sets archive=true, keeps active=true (hides from Active view)
  async archiveTaskList(recipientEmail, taskListTitle) {
    try {
      const response = await apiClient.put('/api/tasks/archive', {
        recipient_email: recipientEmail,
        task_list_title: taskListTitle,
      });
      if (response.data.success) {
        console.log(`🗃️ Task list archived: "${taskListTitle}"`);
        return true;
      } else {
        console.error('❌ Failed to archive task list:', response.data.message);
        return false;
      }
    } catch (error) {
      console.error('❌ archiveTaskList API call failed:', error.message);
      return false;
    }
  }

  // Restore an archived task list — sets archive=false (returns to Active view)
  async restoreTaskList(recipientEmail, taskListTitle) {
    try {
      const response = await apiClient.put('/api/tasks/restore', {
        recipient_email: recipientEmail,
        task_list_title: taskListTitle,
      });
      if (response.data.success) {
        console.log(`✅ Task list restored: "${taskListTitle}"`);
        return true;
      } else {
        console.error('❌ Failed to restore task list:', response.data.message);
        return false;
      }
    } catch (error) {
      console.error('❌ restoreTaskList API call failed:', error.message);
      return false;
    }
  }

  // Remove a task list from all calendars and deactivate its TaskContext record
  async deleteTaskList(recipientEmail, taskListTitle) {
    try {
      const response = await apiClient.post('/api/tasks/delete', {
        recipient_email: recipientEmail,
        task_list_title: taskListTitle,
      });
      if (response.data.success) {
        console.log(`🗑️ Task list deleted: "${taskListTitle}"`);
        return true;
      } else {
        console.error('❌ Failed to delete task list:', response.data.message);
        return false;
      }
    } catch (error) {
      console.error('❌ deleteTaskList API call failed:', error.message);
      return false;
    }
  }

  // Replace the full tasks array for the acting user's own record (draft mode — add/remove tasks).
  // hasUnpublishedChanges / unpublishedChangeCount: the recalculated net dirty state after the change.
  // When provided the backend SETs the exact values instead of blindly incrementing the counter.
  async replaceTasksDraft(taskListTitle, tasks, hasUnpublishedChanges, unpublishedChangeCount) {
    try {
      const body = {
        task_list_title: taskListTitle,
        tasks,
      };
      if (hasUnpublishedChanges !== undefined && unpublishedChangeCount !== undefined) {
        body.has_unpublished_changes  = hasUnpublishedChanges;
        body.unpublished_change_count = unpublishedChangeCount;
      }
      const response = await apiClient.put('/api/tasks/replace-draft', body);
      if (response.data.success) {
        console.log(`📝 Draft replaced: ${response.data.task_count} task(s) for "${taskListTitle}"`);
        return true;
      } else {
        console.error('❌ Failed to replace draft tasks:', response.data.message);
        return false;
      }
    } catch (error) {
      console.error('❌ replaceTasksDraft API call failed:', error.message);
      return false;
    }
  }

  async createTaskList(title, propertyAddress = '', notes = '', connectionId = '') {
    try {
      const response = await apiClient.post('/api/tasks/create', {
        task_list_title:  title,
        property_address: propertyAddress || title,
        notes,
        ...(connectionId ? { connection_id: connectionId } : {}),
      });
      if (response.data.success) {
        console.log(`✅ Created task list "${title}"`);
        return response.data.record;
      } else {
        console.error('❌ Failed to create task list:', response.data.message);
        return { error: response.data.message };
      }
    } catch (error) {
      console.error('❌ createTaskList API call failed:', error.message);
      return null;
    }
  }

  async removeConnectionAPI(accountType, connectionId) {
    try {
      const response = await apiClient.delete(`/api/connections/${accountType}/${connectionId}`);

      if (response.data.success) {
        console.log(`✅ Connection deleted successfully`);
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Backend API delete failed:', error);
      return {
        success: false,
        message: 'Backend API call failed',
        error: error.response?.data?.message || error.message
      };
    }
  }

  // Transform form data to connection format
  transformToConnectionFormat(accountType, members, user, isCompanyCalendarConnection = false, teamNameOverride = null) {
    console.log('[DEBUG] DataService - transformToConnectionFormat called with:');
    console.log('[DEBUG] - accountType:', accountType);
    console.log('[DEBUG] - members.length:', members.length);
    console.log('[DEBUG] - isCompanyCalendarConnection:', isCompanyCalendarConnection);
    console.log('[DEBUG] - typeof isCompanyCalendarConnection:', typeof isCompanyCalendarConnection);
    
    const baseData = {
      user_id: user?.user_id,  // Top-level user_id should be UUID for GSI queries
      current_members: members.length,
      status: 'active'
    };

    // Company calendar flag is just a feature marker, doesn't affect seat counting
    // Members array already includes admin, so just use members.length
    const effectivePaidUsers = members.length;

    // The Company/Brokerage Calendar always belongs to the Account Owner's workspace,
    // regardless of who is editing the team. The backend attaches owner_email to every
    // non-owner user (delegated Admin AND Lead Agent alike — see auth.py), so any acting
    // user who is not the Account Owner themselves resolves to it. Only an actual Account
    // Owner (no owner_email) falls back to their own email. Never derive this from the
    // editor's own email when they are an Admin or Lead Agent delegate.
    const companyCalendarEmail = user?.owner_email || user?.email;

    switch (accountType) {
      case 'small_team':
      case 'medium_team':
      case 'large_team': {
        // Determine max_members and team_type
        let max_members = 6;
        let team_type = 'small_team';
        let description = 'Small Team calendar connection';
        let subscription_plan = 'Small Team';
        if (accountType === 'medium_team') {
          max_members = 8;
          team_type = 'medium_team';
          description = 'Medium Team calendar connection';
          subscription_plan = 'Medium Team';
        } else if (accountType === 'large_team') {
          max_members = 20;
          team_type = 'large_team';
          description = 'Large Team calendar connection';
          subscription_plan = 'Large Team';
        }
        
        // For company calendar connections, adjust description
        if (isCompanyCalendarConnection) {
          description = `${description} - Company Calendar (1 seat)`;
        }
        
        const finalConnectionData = {
          ...baseData,
          team_name: teamNameOverride?.trim() || `${user?.name || 'User'}'s Team`,
          team_type,
          organization_id: user?.organization_id || 'org_default',
          description,
          max_members,
          paid_users: effectivePaidUsers,
          calendars_count: members.length + 1,
          is_company_calendar: isCompanyCalendarConnection,
          owner: {
            user_id: user?.user_id,  // Owner user_id is UUID
            email: user?.email || members[0]?.email,
            account_type: team_type === 'large_team' ? 'Large_Team' : 'Small_Team',
            signup_date: user?.signup_date || new Date().toISOString(),
            is_primary_account: true,
            billing_status: 'paid',
            subscription_plan
          },
          members: members.map((member, idx) => ({
            user_id: this.generateId('user'),
            email: member.email,
            client: member.provider,
            phone: member.phone,
            role: idx === 0 ? 'team_lead' : 'team_member',
            user_type: idx === 0 ? 'primary_account_owner' : 'connection_member',
            is_connection_owner: idx === 0,
            permissions: {
              has_control: idx === 0,
              can_publish_to: ["own_calendar", "company_calendar", "assistant_calendar"],
              can_manage_members: idx === 0,
              can_edit_connection: idx === 0,
            },
            can_delete_task_lists: member.permissions?.can_delete_task_lists ?? member.can_delete_task_lists ?? (idx === 0),
            can_archive_task_lists: member.permissions?.can_archive_task_lists ?? member.can_archive_task_lists ?? (idx === 0),
            can_manage_teams: (member.permissions?.can_manage_teams ?? member.can_manage_teams) === true,
            can_view_unpublished_changes: (member.permissions?.can_view_unpublished_changes ?? member.can_view_unpublished_changes) !== false,
            billing_status: 'paid',
            status: 'active',
            added_date: new Date().toISOString(),
            added_by: idx === 0 ? 'self' : members[0].email,
            task_due_email_reminder: member.task_due_email_reminder !== false,
            personal_calendar_sync: member.personal_calendar_sync !== false,
          }))
        };
        
        // Add company_calendar object if this is a company calendar connection
        if (isCompanyCalendarConnection && members.length > 0) {
          const now = new Date().toISOString();
          finalConnectionData.company_calendar = {
            provider: members[0]?.provider || 'google',
            email: companyCalendarEmail || members[0]?.email,
            name: 'Company Calendar',
            calendar_id: 'primary',
            access_token: 'placeholder_token',
            refresh_token: 'placeholder_refresh_token',
            expires_at: new Date(Date.now() + 3600000).toISOString(),
            scopes: ['calendar.events'],
            calendar_settings: {},
            connected: true,
            created_at: now,
            updated_at: now
          };
          console.log('[DEBUG] Created company_calendar object with provider:', finalConnectionData.company_calendar.provider);
        }
        
        console.log('[DEBUG] Final connection data - paid_users:', effectivePaidUsers, 'is_company_calendar:', isCompanyCalendarConnection);
        
        return finalConnectionData;
      }

      case 'solo_assistant':
        const soloAssistantData = {
          ...baseData,
          agent_name: members[0]?.email?.split('@')[0] || 'Agent',
          team_type: 'single_agent_with_assistant',
          organization_id: user?.organization_id || 'org_default',
          description: isCompanyCalendarConnection ? 
            'Individual agent with assistant support - Company Calendar (1 seat)' : 
            'Individual agent with assistant support',
          max_members: 2,
          paid_users: effectivePaidUsers,
          calendars_count: members.length + 1,
          is_company_calendar: isCompanyCalendarConnection,
          owner: {
            user_id: user?.user_id,  // Owner user_id is UUID
            email: user?.email || members[0]?.email, // This should always be the logged-in user's email
            account_type: 'Solo_Assistant',
            signup_date: user?.signup_date || new Date().toISOString(),
            is_primary_account: true,
            billing_status: 'paid',
            subscription_plan: 'Solo_Assistant'
          },
          members: members.map((member, idx) => ({
            user_id: this.generateId('user'),
            email: member.email,
            client: member.provider,
            phone: member.phone,
            role: idx === 0 ? 'single_agent' : 'assistant',
            user_type: idx === 0 ? 'primary_account_owner' : 'connection_member',
            is_connection_owner: idx === 0,
            permissions: {
              has_control: idx === 0,
              can_publish_to: idx === 0
                ? ["own_calendar", "company_calendar"]
                : ["own_calendar", "company_calendar", "agents_calendars"],
              can_manage_members: idx === 0,
              can_edit_connection: idx === 0,
            },
            can_delete_task_lists: member.permissions?.can_delete_task_lists ?? member.can_delete_task_lists ?? (idx === 0),
            can_archive_task_lists: member.permissions?.can_archive_task_lists ?? member.can_archive_task_lists ?? (idx === 0),
            can_manage_teams: (member.permissions?.can_manage_teams ?? member.can_manage_teams) === true,
            can_view_unpublished_changes: (member.permissions?.can_view_unpublished_changes ?? member.can_view_unpublished_changes) !== false,
            billing_status: 'paid',
            status: 'active',
            added_date: new Date().toISOString(),
            added_by: idx === 0 ? 'self' : members[0].email,
            task_due_email_reminder: member.task_due_email_reminder !== false,
            personal_calendar_sync: member.personal_calendar_sync !== false,
          }))
        };
        
        // Add company_calendar object if this is a company calendar connection
        if (isCompanyCalendarConnection && members.length > 0) {
          const now = new Date().toISOString();
          soloAssistantData.company_calendar = {
            provider: members[0]?.provider || 'google',
            email: companyCalendarEmail || members[0]?.email,
            name: 'Company Calendar',
            calendar_id: 'primary',
            access_token: 'placeholder_token',
            refresh_token: 'placeholder_refresh_token',
            expires_at: new Date(Date.now() + 3600000).toISOString(),
            scopes: ['calendar.events'],
            calendar_settings: {},
            connected: true,
            created_at: now,
            updated_at: now
          };
          console.log('[DEBUG] Created company_calendar object for solo_assistant with provider:', soloAssistantData.company_calendar.provider);
        }
        
        return soloAssistantData;

      case 'individual':
        const individualData = {
          ...baseData,
          user_name: members[0]?.email?.split('@')[0] || 'Individual',
          // Remove connection_type field - let backend handle mapping via file_map
          organization_id: user?.organization_id || 'org_default',
          description: isCompanyCalendarConnection ? 
            'Individual calendar connection - Company Calendar (1 seat)' : 
            'Individual calendar connection',
          max_members: 1,
          paid_users: 1, // Individual connections are always 1 seat
          calendars_count: 1,
          is_company_calendar: isCompanyCalendarConnection,
          owner: {
            user_id: user?.user_id,  // Owner user_id is UUID
            email: user?.email || members[0]?.email,
            account_type: 'Solo',
            signup_date: user?.signup_date || new Date().toISOString(),
            is_primary_account: true,
            billing_status: 'paid',
            subscription_plan: 'Solo'
          },
          members: [
            {
              user_id: this.generateId('user'),
              email: members[0].email,
              client: members[0].provider,
              phone: members[0].phone,
              role: 'individual',
              user_type: 'primary_account_owner',
              is_connection_owner: true,
              permissions: {
                has_control: true,
                can_publish_to: ["own_calendar", "company_calendar"],
                can_manage_members: false,
                can_edit_connection: true
              },
              billing_status: 'paid',
              status: 'active',
              added_date: new Date().toISOString(),
              added_by: 'self'
            }
          ]
        };
        
        // Add company_calendar object if this is a company calendar connection
        if (isCompanyCalendarConnection && members.length > 0) {
          const now = new Date().toISOString();
          individualData.company_calendar = {
            provider: members[0]?.provider || 'google',
            email: companyCalendarEmail || members[0]?.email,
            name: 'Company Calendar',
            calendar_id: 'primary',
            access_token: 'placeholder_token',
            refresh_token: 'placeholder_refresh_token',
            expires_at: new Date(Date.now() + 3600000).toISOString(),
            scopes: ['calendar.events'],
            calendar_settings: {},
            connected: true,
            created_at: now,
            updated_at: now
          };
          console.log('[DEBUG] Created company_calendar object for individual with provider:', individualData.company_calendar.provider);
        }
        
        return individualData;

      case 'solo':
        const soloData = {
          ...baseData,
          user_name: members[0]?.email?.split('@')[0] || 'Solo',
          // Remove connection_type field - let backend handle mapping via file_map
          organization_id: user?.organization_id || 'org_default',
          description: isCompanyCalendarConnection ? 
            'Solo calendar connection - Company Calendar (1 seat)' : 
            'Solo calendar connection',
          max_members: 2,
          paid_users: effectivePaidUsers,
          calendars_count: members.length + 1,
          is_company_calendar: isCompanyCalendarConnection,
          owner: {
            user_id: user?.user_id,  // Owner user_id is UUID
            email: user?.email || members[0]?.email,
            account_type: 'Solo',
            signup_date: user?.signup_date || new Date().toISOString(),
            is_primary_account: true,
            billing_status: 'paid',
            subscription_plan: 'Solo'
          },
          members: members.map((member, idx) => ({
            user_id: this.generateId('user'),
            email: member.email,
            client: member.provider,
            phone: member.phone,
            role: idx === 0 ? 'solo_agent' : 'additional_member',
            user_type: idx === 0 ? 'primary_account_owner' : 'connection_member',
            is_connection_owner: idx === 0,
            permissions: {
              has_control: idx === 0,
              can_publish_to: ["own_calendar", "company_calendar"],
              can_manage_members: idx === 0,
              can_edit_connection: idx === 0,
            },
            can_delete_task_lists: member.permissions?.can_delete_task_lists ?? member.can_delete_task_lists ?? (idx === 0),
            can_archive_task_lists: member.permissions?.can_archive_task_lists ?? member.can_archive_task_lists ?? (idx === 0),
            can_manage_teams: (member.permissions?.can_manage_teams ?? member.can_manage_teams) === true,
            can_view_unpublished_changes: (member.permissions?.can_view_unpublished_changes ?? member.can_view_unpublished_changes) !== false,
            billing_status: 'paid',
            status: 'active',
            added_date: new Date().toISOString(),
            added_by: idx === 0 ? 'self' : members[0].email,
            task_due_email_reminder: member.task_due_email_reminder !== false,
            personal_calendar_sync: member.personal_calendar_sync !== false,
          }))
        };
        
        // Add company_calendar object if this is a company calendar connection
        if (isCompanyCalendarConnection && members.length > 0) {
          const now = new Date().toISOString();
          soloData.company_calendar = {
            provider: members[0]?.provider || 'google',
            email: companyCalendarEmail || members[0]?.email,
            name: 'Company Calendar',
            calendar_id: 'primary',
            access_token: 'placeholder_token',
            refresh_token: 'placeholder_refresh_token',
            expires_at: new Date(Date.now() + 3600000).toISOString(),
            scopes: ['calendar.events'],
            calendar_settings: {},
            connected: true,
            created_at: now,
            updated_at: now
          };
          console.log('[DEBUG] Created company_calendar object for solo with provider:', soloData.company_calendar.provider);
        }
        
        return soloData;

      case 'trial':
        const trialData = {
          ...baseData,
          user_name: members[0]?.email?.split('@')[0] || 'Trial',
          // Remove connection_type field - let backend handle mapping via file_map
          organization_id: user?.organization_id || 'org_default',
          description: isCompanyCalendarConnection ? 
            'Trial calendar connection - Company Calendar (1 seat)' : 
            'Trial calendar connection (14-day free trial)',
          max_members: 2,
          paid_users: effectivePaidUsers,
          calendars_count: members.length + 1,
          is_company_calendar: isCompanyCalendarConnection,
          account_type: 'trial', // Add this field for easy identification
          owner: {
            user_id: user?.user_id,  // Owner user_id is UUID
            email: user?.email || members[0]?.email,
            account_type: 'Trial',
            signup_date: user?.signup_date || new Date().toISOString(),
            is_primary_account: true,
            billing_status: 'trial',
            subscription_plan: 'Trial'
          },
          members: members.map((member, idx) => ({
            user_id: this.generateId('user'),
            email: member.email,
            client: member.provider,
            phone: member.phone,
            role: idx === 0 ? 'trial_user' : 'additional_member',
            user_type: idx === 0 ? 'primary_account_owner' : 'connection_member',
            is_connection_owner: idx === 0,
            permissions: {
              has_control: idx === 0,
              can_publish_to: ["own_calendar", "company_calendar"],
              can_manage_members: idx === 0,
              can_edit_connection: idx === 0,
            },
            can_delete_task_lists: member.permissions?.can_delete_task_lists ?? member.can_delete_task_lists ?? (idx === 0),
            can_archive_task_lists: member.permissions?.can_archive_task_lists ?? member.can_archive_task_lists ?? (idx === 0),
            can_manage_teams: (member.permissions?.can_manage_teams ?? member.can_manage_teams) === true,
            can_view_unpublished_changes: (member.permissions?.can_view_unpublished_changes ?? member.can_view_unpublished_changes) !== false,
            billing_status: 'trial',
            status: 'active',
            added_date: new Date().toISOString(),
            added_by: idx === 0 ? 'self' : members[0].email,
            task_due_email_reminder: member.task_due_email_reminder !== false,
            personal_calendar_sync: member.personal_calendar_sync !== false,
          }))
        };
        
        // Add company_calendar object if this is a company calendar connection
        if (isCompanyCalendarConnection && members.length > 0) {
          const now = new Date().toISOString();
          trialData.company_calendar = {
            provider: members[0]?.provider || 'google',
            email: companyCalendarEmail || members[0]?.email,
            name: 'Company Calendar',
            calendar_id: 'primary',
            access_token: 'placeholder_token',
            refresh_token: 'placeholder_refresh_token',
            expires_at: new Date(Date.now() + 3600000).toISOString(),
            scopes: ['calendar.events'],
            calendar_settings: {},
            connected: true,
            created_at: now,
            updated_at: now
          };
          console.log('[DEBUG] Created company_calendar object for trial with provider:', trialData.company_calendar.provider);
        }
        
        return trialData;

      case 'super':
      case 'brokerage':
        return {
          ...baseData,
          organization_name: user?.organization_name || `${user?.name || 'User'}'s Organization`,
          team_type: accountType,
          organization_id: user?.organization_id || 'org_default',
          description: accountType === 'super' ? 
            (isCompanyCalendarConnection ? 
              'Super Admin account - Company Calendar (1 seat)' : 
              'Super Admin account with unlimited connections') :
            (isCompanyCalendarConnection ? 
              'Brokerage account - Company Calendar (1 seat)' : 
              'Brokerage account with multiple team connections'),
          max_members: 999, // Unlimited for super/brokerage
          paid_users: effectivePaidUsers,
          calendars_count: members.length + 1,
          is_company_calendar: isCompanyCalendarConnection,
          owner: {
            user_id: user?.user_id,
            email: user?.email || members[0]?.email,
            account_type: accountType === 'super' ? 'Super' : 'Brokerage',
            signup_date: user?.signup_date || new Date().toISOString(),
            is_primary_account: true,
            billing_status: 'paid',
            subscription_plan: accountType === 'super' ? 'Super Admin' : 'Brokerage'
          },
          members: members.map((member, idx) => ({
            user_id: this.generateId('user'),
            email: member.email,
            client: member.provider,
            phone: member.phone,
            role: idx === 0 ? 'admin' : 'member',
            user_type: idx === 0 ? 'primary_account_owner' : 'connection_member',
            is_connection_owner: idx === 0,
            permissions: {
              has_control: idx === 0,
              can_publish_to: ["own_calendar", "company_calendar", "all_calendars"],
              can_manage_members: true,
              can_edit_connection: true,
              is_admin: idx === 0
            },
            billing_status: 'paid',
            status: 'active',
            added_date: new Date().toISOString(),
            added_by: idx === 0 ? 'self' : members[0].email
          }))
        };

      default:
        return baseData;
    }
  }
}

// Export singleton instance
const dataService = new DataService();
export default dataService;
